import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  CrmOperationType,
  CrmPaymentWithDocumentEntity,
} from '../../../../../../api/types/crm/payments/crm-payments-entity';
import { AddCrmPaymentDialog } from './components/add-crm-payment-dialog';
import { CreditCardTokenEntity } from '../../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { Tag } from 'primereact/tag';
import { PaymentMethodsDb } from '../../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface AccountPaymentsProps {
  accountId: number | null;
  payments: CrmPaymentWithDocumentEntity[];
  loading: boolean;
  onRefresh: () => void;
  creditCardTokens?: CreditCardTokenEntity[];
  /** Default «לכבוד» on the receipt — typically the CRM account display name */
  defaultReceiptRecipientName?: string;
  /** Default registration number on the receipt — CRM account registrationNumber */
  defaultRegistrationNumber?: string;
  /** Default phone on the receipt — typically contacts[0]?.phone */
  defaultPhoneNumber?: string;
}

const paymentMethodHebrew = (method: PaymentMethodsDb | null | undefined) => {
  switch (method) {
    case PaymentMethodsDb.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethodsDb.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethodsDb.Check:
      return 'המחאה';
    case PaymentMethodsDb.Scholarship:
      return 'מלגה';
    case PaymentMethodsDb.Cash:
      return 'מזומן';
    default:
      return 'חסר';
  }
};

const optionalTextCell = (value: PaymentMethodsDb | null | undefined) => {
  return paymentMethodHebrew(value);
};

const operationTypeHebrew = (value: string | null | undefined) => {
  const normalized = (value ?? '').toLowerCase();
  if (normalized === 'payment') return 'תשלום';
  if (normalized === 'refund') return 'החזר';
  return optionalTextCell(value);
};

export const AccountPayments: React.FC<AccountPaymentsProps> = ({
  accountId,
  payments,
  loading,
  onRefresh,
  creditCardTokens = [],
  defaultReceiptRecipientName = '',
  defaultRegistrationNumber = '',
  defaultPhoneNumber = '',
}) => {
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  // The row being refunded; when non-null the refund dialog is open. We keep
  // the row (rather than just the id) so the dialog can derive the synthetic
  // service line and original-payment header from it without an extra fetch.
  const [refundForRow, setRefundForRow] =
    useState<CrmPaymentWithDocumentEntity | null>(null);

  const sortedPayments = useMemo(() => {
    const copy = [...(payments ?? [])];
    copy.sort((a, b) => b.id - a.id);
    return copy;
  }, [payments]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>תשלומים</div>
        <Button
          label="הוספת תשלום"
          icon="pi pi-plus"
          onClick={() => setShowAddDialog(true)}
          disabled={!accountId}
        />
      </div>

      <DataTable
        value={sortedPayments}
        loading={loading}
        dataKey="id"
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו תשלומים</div>
        }
        style={{ direction: 'rtl' }}
      >
        <Column
          field="branchName"
          header="שלוחה"
          style={{ textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) =>
            row.branchName?.trim() ? row.branchName : 'לא קיים'
          }
        />
        <Column
          field="paymentAmount"
          header="סכום"
          style={{ textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) => {
            if (typeof row.paymentAmount !== 'number') {
              return 'חסר';
            }
            const isRefund =
              (row.operationType ?? '').toLowerCase() === 'refund';
            const color = isRefund ? '#F59E0B' : '#10B981'; // orange for refund, green for payment
            return (
              <span style={{ color, fontWeight: 600 }}>
                {row.paymentAmount.toFixed(2)}
              </span>
            );
          }}
        />
        <Column
          field="paymentMethod"
          header="אמצעי תשלום"
          style={{ textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) =>
            optionalTextCell(row.paymentMethodType)
          }
        />
        <Column
          field="operationType"
          header="סוג פעולה"
          style={{ textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) => {
            const normalized = (row.operationType ?? '').toLowerCase();
            const isRefund = normalized === 'refund';
            const label = operationTypeHebrew(row.operationType);
            const severity = isRefund ? 'warning' : 'success';
            return <Tag value={label} severity={severity as any} />;
          }}
        />
        <Column
          header="מסמך"
          style={{ width: '200px', textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) => {
            if (!row.fileUrl) {
              return <span style={{ color: '#6B7280' }}>לא מצורף מסמך</span>;
            }
            return (
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '0.375rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.fileUrl as string, '_blank');
                }}
              >
                <i
                  className="pi pi-download"
                  style={{ fontSize: '1rem', marginRight: '0.5rem' }}
                />
                לחץ לצפייה
              </button>
            );
          }}
        />
        <Column
          header=""
          style={{ width: '100px', textAlign: 'right' }}
          body={(row: CrmPaymentWithDocumentEntity) => {
            // Refunds can only originate from a payment row. Hiding the button
            // on refund rows mirrors the backend rule that
            // `originalCrmPaymentId` must point at a Payment, not a Refund.
            if (row.operationType !== CrmOperationType.Payment) return null;
            return (
              <Button
                icon="pi pi-undo"
                text
                severity="warning"
                label="החזר"
                tooltip="יצירת החזר עבור תשלום זה"
                tooltipOptions={{ position: 'top' }}
                disabled={!accountId}
                onClick={() => setRefundForRow(row)}
              />
            );
          }}
        />
      </DataTable>

      <AddCrmPaymentDialog
        visible={showAddDialog}
        mode="create-payment"
        accountId={accountId}
        onHide={() => setShowAddDialog(false)}
        onSuccess={onRefresh}
        creditCardTokens={creditCardTokens}
        defaultReceiptRecipientName={defaultReceiptRecipientName}
        defaultRegistrationNumber={defaultRegistrationNumber}
        defaultPhoneNumber={defaultPhoneNumber}
      />

      {/* Mounted as a separate instance so the refund's pre-filled state
          (synthetic service line, original-payment header) doesn't leak into
          the create-payment dialog and vice versa. */}
      <AddCrmPaymentDialog
        visible={refundForRow !== null}
        mode="refund"
        originalPaymentRow={refundForRow}
        accountId={accountId}
        onHide={() => setRefundForRow(null)}
        onSuccess={() => {
          onRefresh();
          setRefundForRow(null);
        }}
        creditCardTokens={creditCardTokens}
        defaultReceiptRecipientName={defaultReceiptRecipientName}
        defaultRegistrationNumber={defaultRegistrationNumber}
        defaultPhoneNumber={defaultPhoneNumber}
      />
    </div>
  );
};
